import type {
  VideoAnalysis,
  VideoAnalysisProvider,
  VideoListItem,
} from '@sr-test-wizard/shared';
import { api } from './api';

/**
 * Wrappers for the `/api/runs/:id/videos` route module added by the
 * gameplay-video analyser (Extras tool). Uploads use a multipart request,
 * everything else is plain JSON. Stream / frame URLs are returned as-is
 * so the HTML5 `<video>` element and `<img>` open-in-tab links can point
 * straight at them.
 */

export interface RunVideosListResponse {
  videosPath: string | null;
  items: VideoListItem[];
}

export interface VideoAnalysisResponse {
  exists: boolean;
  provider: VideoAnalysisProvider;
  availableProviders: Record<VideoAnalysisProvider, boolean>;
  analysis: VideoAnalysis | null;
}

export interface VideoUploadResult {
  slug: string;
  videoFile: string;
  sizeBytes: number;
  uploadedAt: string;
  frameCount: number;
  durationSeconds: number | null;
  analysisExists: boolean;
  analysisByProvider: Record<VideoAnalysisProvider, boolean>;
  extractInProgress: boolean;
  analyzeInProgress: boolean;
}

async function uploadFile(
  runId: string,
  file: File,
  onProgress?: (percent: number) => void,
): Promise<VideoUploadResult> {
  // Plain `fetch` doesn't expose upload progress; we use XHR specifically
  // to drive the upload bar in the UI.
  return new Promise<VideoUploadResult>((resolveFn, rejectFn) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `/api/runs/${runId}/videos`);
    xhr.withCredentials = true;
    xhr.upload.onprogress = (evt) => {
      if (!onProgress || !evt.lengthComputable) return;
      onProgress(Math.round((evt.loaded / evt.total) * 100));
    };
    xhr.onload = () => {
      try {
        const parsed = JSON.parse(xhr.responseText || '{}');
        if (xhr.status >= 200 && xhr.status < 300 && parsed?.success) {
          resolveFn(parsed.data as VideoUploadResult);
        } else {
          rejectFn(new Error(parsed?.error || `Upload failed (${xhr.status})`));
        }
      } catch (err) {
        rejectFn(new Error(`Upload failed (${xhr.status}): ${(err as Error).message}`));
      }
    };
    xhr.onerror = () => rejectFn(new Error('Network error during upload'));
    xhr.onabort = () => rejectFn(new Error('Upload aborted'));
    const fd = new FormData();
    fd.append('video', file);
    xhr.send(fd);
  });
}

export interface DriveFolderEvidence {
  available: boolean;
  folderId: string | null;
  fileCount: number;
  path: string;
  error?: string;
  files?: Array<{
    id: string;
    name: string;
    mimeType: string;
    sizeBytes?: number | null;
  }>;
}

export interface DriveEvidenceProbe {
  texecFound: boolean;
  texecPath: string;
  driveId: string | null;
  gameRules: DriveFolderEvidence;
  gameplay: DriveFolderEvidence;
  error?: string;
}

export const runVideosApi = {
  list: (runId: string) =>
    api.get<RunVideosListResponse>(`/api/runs/${runId}/videos`),
  driveEvidence: (runId: string) =>
    api.get<DriveEvidenceProbe>(`/api/runs/${runId}/videos/drive-evidence`),
  upload: uploadFile,
  fromDrive: (runId: string, driveUrl: string) =>
    api.post<{ started: boolean; jobId: string; jobIds?: string[]; count?: number }>(
      `/api/runs/${runId}/videos/from-drive`,
      { driveUrl },
    ),
  fromDriveFiles: (runId: string, fileIds: string[]) =>
    api.post<{ started: boolean; jobId: string | null; jobIds: string[]; count: number }>(
      `/api/runs/${runId}/videos/from-drive`,
      { fileIds },
    ),
  delete: (runId: string, slug: string) =>
    api.delete<{ slug: string }>(`/api/runs/${runId}/videos/${encodeURIComponent(slug)}`),
  extract: (runId: string, slug: string) =>
    api.post<{ slug: string; started: boolean }>(
      `/api/runs/${runId}/videos/${encodeURIComponent(slug)}/extract`,
    ),
  analyze: (
    runId: string,
    slug: string,
    force = false,
    provider: VideoAnalysisProvider = 'openai-frames',
  ) =>
    api.post<{ slug: string; provider: VideoAnalysisProvider; started: boolean }>(
      `/api/runs/${runId}/videos/${encodeURIComponent(slug)}/analyze`,
      { force, provider },
    ),
  analysis: (
    runId: string,
    slug: string,
    provider: VideoAnalysisProvider = 'openai-frames',
  ) =>
    api.get<VideoAnalysisResponse>(
      `/api/runs/${runId}/videos/${encodeURIComponent(slug)}/analysis?provider=${encodeURIComponent(provider)}`,
    ),
  status: (
    runId: string,
    slug: string,
    provider: VideoAnalysisProvider = 'openai-frames',
  ) =>
    api.get<{
      slug: string;
      frameCount: number;
      analysisExists: boolean;
      analysisByProvider: Record<VideoAnalysisProvider, boolean>;
      durationSeconds: number | null;
      extractInProgress: boolean;
      analyzeInProgress: boolean;
      provider: VideoAnalysisProvider;
    }>(`/api/runs/${runId}/videos/${encodeURIComponent(slug)}/status?provider=${encodeURIComponent(provider)}`),
  streamUrl: (runId: string, slug: string): string =>
    `/api/runs/${runId}/videos/${encodeURIComponent(slug)}/stream`,
  frameUrl: (runId: string, slug: string, frame: string): string =>
    `/api/runs/${runId}/videos/${encodeURIComponent(slug)}/frames/${encodeURIComponent(frame)}`,
};
